import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navigation from '../Navigation';
import SimpleNavigation from '../SimpleNavigation';
import Footer from '../Footer/Footer';
import SimpleFooter from '../SimpleFooter';
import AdminImpersonationBanner from '../AdminImpersonationBanner';
import { useImpersonation } from '../../contexts/ImpersonationContext';
import styles from './Layout.module.scss';
const SIMPLE_NAV_ROUTES = ['/account'];
const SIMPLE_FOOTER_ROUTES = ['/account'];
const POLL_INTERVAL_FAST_MS = 15000;
const POLL_INTERVAL_NORMAL_MS = 60000;
const POLL_INTERVAL_SLOW_MS = 300000;
const POLL_INTERVAL_IDLE_MS = 600000;
const getNextPollIntervalMs = (status) => {
    if (typeof document !== 'undefined' && document.hidden) {
        return null;
    }
    if (!status) {
        return POLL_INTERVAL_SLOW_MS;
    }
    if (status.isMaintenanceActive) {
        return POLL_INTERVAL_SLOW_MS;
    }
    if (!status.maintenanceStartsAt) {
        return POLL_INTERVAL_IDLE_MS;
    }
    const startMs = new Date(status.maintenanceStartsAt).getTime();
    const diffMs = startMs - Date.now();
    if (diffMs <= 5 * 60 * 1000) {
        return POLL_INTERVAL_FAST_MS;
    }
    if (diffMs <= 30 * 60 * 1000) {
        return POLL_INTERVAL_NORMAL_MS;
    }
    if (status.showBanner) {
        return POLL_INTERVAL_NORMAL_MS;
    }
    if (diffMs <= 24 * 60 * 60 * 1000) {
        return POLL_INTERVAL_SLOW_MS;
    }
    return POLL_INTERVAL_IDLE_MS;
};
const Layout = ({ children, excludeNavigation = false, excludeFooter = false, }) => {
    const location = useLocation();
    const { isImpersonating } = useImpersonation();
    const [maintenanceStatus, setMaintenanceStatus] = useState(null);
    const [countdownLabel, setCountdownLabel] = useState('');
    const [impersonationBannerHeight, setImpersonationBannerHeight] = useState(0);
    const impersonationBannerRef = useRef(null);
    const pollTimeoutRef = useRef(null);
    const isMountedRef = useRef(false);
    const statusRef = useRef(null);
    const useSimpleNav = SIMPLE_NAV_ROUTES.some((route) => location.pathname.startsWith(route));
    const useSimpleFooter = SIMPLE_FOOTER_ROUTES.some((route) => location.pathname.startsWith(route));
    // Show impersonation banner only on non-admin pages
    const isAdminPage = location.pathname.startsWith('/web-admin');
    const showImpersonationBanner = isImpersonating && !isAdminPage;
    // The banner is position: fixed (it sits below the nav, above page content) so it doesn't
    // push anything down on its own - measure its actual rendered height and add that as top
    // padding on the content area below, so the banner never overlaps/hides the first section
    // of the page. Measured (not a hardcoded constant) so it stays correct if the banner's
    // content, font size, or wrapping changes.
    useLayoutEffect(() => {
        if (!showImpersonationBanner || !impersonationBannerRef.current) {
            setImpersonationBannerHeight(0);
            return;
        }
        const bannerEl = impersonationBannerRef.current;
        const updateHeight = () => setImpersonationBannerHeight(bannerEl.offsetHeight);
        updateHeight();
        const resizeObserver = new ResizeObserver(updateHeight);
        resizeObserver.observe(bannerEl);
        return () => resizeObserver.disconnect();
    }, [showImpersonationBanner]);
    // Reset scroll position to top on route changes
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        }
    }, [location.pathname]);
    useEffect(() => {
        isMountedRef.current = true;
        const clearPollTimeout = () => {
            if (pollTimeoutRef.current !== null) {
                window.clearTimeout(pollTimeoutRef.current);
                pollTimeoutRef.current = null;
            }
        };
        const scheduleNextPoll = (status) => {
            clearPollTimeout();
            const nextIntervalMs = getNextPollIntervalMs(status);
            if (nextIntervalMs === null) {
                return;
            }
            pollTimeoutRef.current = window.setTimeout(() => {
                void fetchMaintenanceStatus();
            }, nextIntervalMs);
        };
        const fetchMaintenanceStatus = async () => {
            try {
                const response = await fetch('/api/v1/maintenance-status/', {
                    credentials: 'include',
                });
                if (!response.ok) {
                    scheduleNextPoll(statusRef.current);
                    return;
                }
                const data = await response.json();
                if (isMountedRef.current) {
                    statusRef.current = data;
                    setMaintenanceStatus(data);
                }
                scheduleNextPoll(data);
            }
            catch {
                // Ignore banner fetch errors to avoid impacting page behavior.
                scheduleNextPoll(statusRef.current);
            }
        };
        const onVisibilityChange = () => {
            if (typeof document !== 'undefined' && document.hidden) {
                clearPollTimeout();
                return;
            }
            void fetchMaintenanceStatus();
        };
        void fetchMaintenanceStatus();
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            isMountedRef.current = false;
            clearPollTimeout();
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, []);
    useEffect(() => {
        if (!maintenanceStatus?.showBanner || !maintenanceStatus?.showCountdown || !maintenanceStatus?.maintenanceStartsAt) {
            setCountdownLabel('');
            return;
        }
        const calculateLabel = () => {
            const target = new Date(maintenanceStatus.maintenanceStartsAt).getTime();
            const now = Date.now();
            const remainingMs = target - now;
            if (remainingMs <= 0) {
                setCountdownLabel('Starting now');
                return;
            }
            const totalSeconds = Math.floor(remainingMs / 1000);
            const days = Math.floor(totalSeconds / 86400);
            const hours = Math.floor((totalSeconds % 86400) / 3600);
            const minutes = Math.floor((totalSeconds % 3600) / 60);
            if (days > 0) {
                setCountdownLabel(`${days}d ${hours}h ${minutes}m`);
            }
            else if (hours > 0) {
                setCountdownLabel(`${hours}h ${minutes}m`);
            }
            else {
                setCountdownLabel(`${minutes}m`);
            }
        };
        calculateLabel();
        const intervalId = window.setInterval(calculateLabel, 1000);
        return () => window.clearInterval(intervalId);
    }, [maintenanceStatus]);
    const formattedMaintenanceStart = useMemo(() => {
        if (!maintenanceStatus?.maintenanceStartsAt) {
            return '';
        }
        try {
            return new Date(maintenanceStatus.maintenanceStartsAt).toLocaleString();
        }
        catch {
            return maintenanceStatus.maintenanceStartsAt;
        }
    }, [maintenanceStatus]);
    const shouldShowMaintenanceBanner = Boolean(maintenanceStatus?.showBanner && maintenanceStatus.announcementMessage);
    return (_jsxs("div", { className: styles.layoutWrapper, children: [shouldShowMaintenanceBanner && (_jsxs("div", { className: `${styles.maintenanceBanner} ${!excludeNavigation ? styles.maintenanceBannerWithNav : ''}`, children: [_jsx("p", { className: styles.maintenanceBanner__message, children: maintenanceStatus?.announcementMessage }), formattedMaintenanceStart && (_jsxs("p", { className: styles.maintenanceBanner__schedule, children: ["Scheduled start: ", formattedMaintenanceStart, maintenanceStatus?.displayTimezone ? ` (${maintenanceStatus.displayTimezone})` : ''] })), maintenanceStatus?.showCountdown && countdownLabel && (_jsxs("p", { className: styles.maintenanceBanner__countdown, children: ["Maintenance starts in ", countdownLabel] }))] })), !excludeNavigation && (useSimpleNav ? _jsx(SimpleNavigation, {}) : _jsx(Navigation, {})), _jsx("main", { className: styles.layoutWrapper__container, style: showImpersonationBanner ? { paddingTop: impersonationBannerHeight } : undefined, children: children }), !excludeFooter && (useSimpleFooter ? _jsx(SimpleFooter, {}) : _jsx(Footer, {})), showImpersonationBanner && _jsx(AdminImpersonationBanner, { ref: impersonationBannerRef })] }));
};
export default Layout;
